import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Clipboard, Headphones, ImagePlus, Mic, RotateCcw, X } from "lucide-react";
import { aiAgentAPI } from "../../../services/apiService";
import { ChoiceRow, Drawer, DrawerField, DrawerGroup, drawerInput } from "../Drawer";
import { InlineLoader } from "../PageLoader";
import { PrimaryButton } from "../ui";
import { AccessNotice, fetchApi, reasonOf } from "../marketing/access";
import { WIDGET_BASE_URL, errorMessage, parseJson, resolveUrl } from "./kit";

/**
 * Voice widget settings for one web-voice agent (replaces the old VoiceWidgetConfigurator and
 * VoiceEmbedCodeSection, same calls and fields).
 *
 *   save ........... PUT  /api/ai-agent/config/:id { voice_widget_config }
 *   avatar / icon .. POST /api/ai-agent/config/:id/widget-upload?type=avatar|icon (field "image";
 *                    the server takes JPEG, PNG, GIF, WebP or SVG up to 2 MB and returns { url })
 *   embed code ..... the agent's key from GET /api/ai-agent/config/:id/api-key, on /widget/v1/olli-voice-widget.js
 * The public widget reads the same keys (public/widget/v1/olli-voice-widget.js).
 */

export const VOICE_WIDGET_DEFAULTS = {
  primary_color: "#667eea",
  background_color: "#1a1a2e",
  text_color: "#ffffff",
  secondary_color: "#a0a0b8",
  position: "bottom-right",
  button_size: 60,
  show_branding: true,
  orb_avatar: "",
  button_icon: "",
};

const COLORS = [
  { key: "primary_color", label: "Accent color", hint: "The orb, mic button and floating button." },
  { key: "background_color", label: "Background color", hint: "The panel background." },
  { key: "text_color", label: "Text color", hint: "Main text and icons." },
  { key: "secondary_color", label: "Secondary color", hint: "Subtitle, hints and timer." },
];
const POSITIONS = { "bottom-right": "Bottom right", "bottom-left": "Bottom left" };
const UPLOAD_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
const MAX_UPLOAD = 2 * 1024 * 1024;
const isHex = (v) => /^#[0-9a-f]{6}$/i.test(String(v || ""));

const PLATFORMS = [
  { id: "html", label: "HTML", hint: "Paste this single line just before the closing </body> tag in your HTML file." },
  { id: "wordpress", label: "WordPress", hint: "Install the \"WPCode\" plugin (or any code snippets plugin). Go to Code Snippets > Add Snippet > choose \"HTML Snippet\". Paste the code, set location to \"Site Wide Footer\", and activate it." },
  { id: "shopify", label: "Shopify", hint: "Go to Online Store > Themes > click \"...\" > Edit Code. Open theme.liquid and paste this single line just before the closing </body> tag." },
  { id: "react", label: "React / Next.js", hint: "For React (CRA): paste in public/index.html before </body>. For Next.js (App Router): add in app/layout.js (or .tsx) using next/script: <Script src=\"...\" data-api-key=\"...\" strategy=\"afterInteractive\" />. For Next.js (Pages Router): same approach in pages/_app.js (or .tsx)." },
  { id: "wix", label: "Wix / Squarespace", hint: "Wix: Go to Settings > Custom Code > + Add Code, set placement to \"Body - End\". Squarespace: Go to Settings > Advanced > Code Injection, paste in the Footer field." },
];
const embedCodeFor = (key) => `<!-- Olli Voice Widget -->\n<script src="${WIDGET_BASE_URL}/widget/v1/olli-voice-widget.js" data-api-key="${key || "YOUR_API_KEY"}" async></script>`;

const fromAgent = (agent) => ({ ...VOICE_WIDGET_DEFAULTS, ...(parseJson(agent.voice_widget_config, {}) || {}) });

/** Swatch + hex. The swatch opens the system picker; the hex field takes a typed #rrggbb. */
function ColorField({ id, label, hint, value, onChange }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  const bad = !isHex(text);
  return <DrawerField label={label} htmlFor={id} error={bad ? "Use a hex color like #667eea" : null} hint={hint}>
    <div className="flex items-center gap-2.5">
      <label className="relative flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full ring-1 ring-line hover:ring-ink/40 focus-within:ring-2 focus-within:ring-ink/30" style={{ backgroundColor: isHex(value) ? value : "transparent" }}>
        <span className="sr-only">Pick {label.toLowerCase()}</span>
        <input type="color" value={isHex(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
      </label>
      <input id={id} value={text} maxLength={7} aria-invalid={bad} spellCheck={false} onChange={(e) => { const v = e.target.value.trim(); setText(v); if (isHex(v)) onChange(v.toLowerCase()); }} className={`${drawerInput} min-w-0 font-mono uppercase`} />
    </div>
  </DrawerField>;
}

/** Image upload row: preview, Upload/Change, Remove. */
function AssetField({ label, hint, url, uploading, onPick, onRemove, round = true }) {
  const input = useRef(null);
  return <DrawerField label={label} hint={hint}>
    <div className="flex flex-wrap items-center gap-3">
      {url ? <img src={url} alt="" className={`h-12 w-12 object-cover ring-1 ring-line ${round ? "rounded-full" : "rounded-sm"}`} /> : <span className="flex h-12 w-12 items-center justify-center rounded-full bg-mist text-muted"><ImagePlus size={18} strokeWidth={1.75} /></span>}
      <button type="button" disabled={uploading} onClick={() => input.current?.click()} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-mist px-3.5 text-caption font-semibold text-ink hover:bg-line disabled:opacity-60">{uploading && <InlineLoader size={14} />}{uploading ? "Uploading..." : url ? "Change" : `Upload ${label.toLowerCase()}`}</button>
      {url && !uploading && <button type="button" onClick={onRemove} className="inline-flex h-9 items-center rounded-full px-3.5 text-caption font-semibold text-danger hover:bg-danger-soft">Remove</button>}
      <input ref={input} type="file" accept="image/*" className="sr-only" tabIndex={-1} onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  </DrawerField>;
}

const MicIcon = ({ fill, size }) => <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" fill={fill} /><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" fill={fill} /></svg>;

/** The voice panel and floating button as a visitor sees them, in the clinic's colours. */
export function VoiceWidgetPreview({ config, avatarUrl, iconUrl, agentName }) {
  const pc = config.primary_color || VOICE_WIDGET_DEFAULTS.primary_color;
  const bg = config.background_color || VOICE_WIDGET_DEFAULTS.background_color;
  const tc = config.text_color || VOICE_WIDGET_DEFAULTS.text_color;
  const sc = config.secondary_color || VOICE_WIDGET_DEFAULTS.secondary_color;
  const side = config.position === "bottom-left" ? "left-4" : "right-4";
  const fab = Math.round((config.button_size || 60) * 0.7);
  return <div className="relative min-h-[380px] overflow-hidden rounded-lg bg-mist/60 p-4" aria-label="Voice widget preview">
    <div className="flex h-8 items-center gap-1.5 rounded-sm bg-paper px-3"><span className="h-2 w-2 rounded-full bg-danger" /><span className="h-2 w-2 rounded-full bg-sun" /><span className="h-2 w-2 rounded-full bg-success" /><span className="ml-4 h-3.5 flex-1 rounded-full bg-mist" /></div>
    <div className="mt-6 flex flex-col gap-2 px-2">{["w-4/5", "w-3/5", "w-11/12", "w-2/5"].map((w) => <span key={w} className={`h-2 rounded-full bg-paper ${w}`} />)}</div>
    <div className={`absolute bottom-[72px] ${side} w-[184px] overflow-hidden rounded-lg shadow-lift`} style={{ background: bg }}>
      <div className="flex flex-col items-center px-4 pb-4 pt-3">
        <div className="mb-3 flex w-full items-center justify-between font-mono text-[10px]" style={{ color: sc }}><span>00:00</span><span aria-hidden="true">—</span></div>
        <div className="mb-2 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full shadow-soft" style={{ background: `radial-gradient(circle at 35% 35%, ${pc}, ${pc}cc)` }}>{avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" /> : <MicIcon fill={`${tc}e6`} size={28} />}</div>
        <p className="mt-1 max-w-full truncate text-caption font-semibold" style={{ color: tc }}>{agentName || "Voice Assistant"}</p>
        <p className="mt-0.5 text-[10px]" style={{ color: sc }}>Click the mic to start</p>
        <div className="mt-3 flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center rounded-full shadow-soft" style={{ background: pc }}><MicIcon fill={tc} size={16} /></span><span className="flex h-6 w-6 items-center justify-center rounded-full bg-paper/10"><X size={13} strokeWidth={2} style={{ color: sc }} /></span></div>
        {config.show_branding && <p className="mt-3 text-[9px] opacity-60" style={{ color: sc }}>Powered by Olli AI</p>}
      </div>
    </div>
    <div className={`absolute bottom-4 ${side} flex items-center justify-center overflow-hidden rounded-full shadow-lift`} style={{ width: fab, height: fab, background: iconUrl ? "transparent" : pc }}>{iconUrl ? <img src={iconUrl} alt="" className="h-full w-full rounded-full object-cover" /> : <Headphones size={18} strokeWidth={2} style={{ color: tc }} />}</div>
  </div>;
}

export function VoiceWidgetSettings({ agent, onClose, onSaved }) {
  const [config, setConfig] = useState(() => fromAgent(agent));
  const [baseline, setBaseline] = useState(() => JSON.stringify(fromAgent(agent)));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState("");
  const [previews, setPreviews] = useState({});
  const [apiKey, setApiKey] = useState("");
  const [keyLoading, setKeyLoading] = useState(true);
  const [keyReason, setKeyReason] = useState(null);
  const [platform, setPlatform] = useState("html");
  const [copied, setCopied] = useState(false);

  const loadKey = useCallback(async () => {
    setKeyLoading(true);
    const res = await fetchApi(`/api/ai-agent/config/${agent.id}/api-key`);
    setKeyReason(reasonOf(res));
    if (res.ok) setApiKey(res.data.api_key || "");
    setKeyLoading(false);
  }, [agent.id]);
  useEffect(() => { loadKey(); }, [loadKey]);
  useEffect(() => () => Object.values(previews).forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const update = (field, value) => { setConfig((prev) => ({ ...prev, [field]: value })); setSaved(false); };
  const dirty = JSON.stringify(config) !== baseline;
  const invalid = COLORS.some((c) => !isHex(config[c.key]));

  const upload = async (field, type, file) => {
    if (!file) return;
    if (!UPLOAD_TYPES.includes(file.type)) { setError("Use a JPEG, PNG, GIF, WebP or SVG image."); return; }
    if (file.size > MAX_UPLOAD) { setError("That image is over 2 MB. Pick a smaller one."); return; }
    const local = URL.createObjectURL(file);
    setPreviews((p) => ({ ...p, [field]: local }));
    setUploading(field); setError("");
    try {
      const res = await aiAgentAPI.uploadWidgetAsset(agent.id, type, file);
      if (res.data.success) update(field, res.data.url);
      else setError(res.data.message || `Failed to upload ${type === "avatar" ? "avatar" : "button icon"}`);
    } catch (err) {
      setError(`Failed to upload ${type === "avatar" ? "avatar" : "button icon"}: ${errorMessage(err, err.message)}`);
    } finally {
      setPreviews((p) => { const next = { ...p }; delete next[field]; return next; });
      setUploading("");
    }
  };

  const save = async () => {
    if (saving || invalid) return;
    setSaving(true); setError("");
    try {
      const res = await aiAgentAPI.updateVoiceWidgetConfig(agent.id, config);
      if (!res.data.success) throw new Error(res.data.message || "Failed to save voice widget config");
      setBaseline(JSON.stringify(config)); setSaved(true);
      onSaved?.(config);
      window.setTimeout(() => setSaved(false), 2400);
    } catch (err) {
      setError(errorMessage(err, err.message || "Failed to save voice widget config"));
    } finally {
      setSaving(false);
    }
  };
  const reset = () => { setConfig({ ...VOICE_WIDGET_DEFAULTS }); setSaved(false); setError(""); };

  const code = embedCodeFor(apiKey);
  const copyCode = async () => {
    try { await navigator.clipboard.writeText(code); }
    catch { const area = document.createElement("textarea"); area.value = code; document.body.appendChild(area); area.select(); document.execCommand("copy"); area.remove(); }
    setCopied(true); window.setTimeout(() => setCopied(false), 2000);
  };
  const avatarUrl = previews.orb_avatar || resolveUrl(config.orb_avatar);
  const iconUrl = previews.button_icon || resolveUrl(config.button_icon);
  const hint = PLATFORMS.find((p) => p.id === platform)?.hint;

  return <Drawer open onClose={onClose} title="Voice widget" context={agent.agent_name} width="lg" dirty={dirty}
    danger={<button type="button" onClick={reset} disabled={saving} className="inline-flex h-11 items-center gap-1.5 rounded-full px-4 text-small font-semibold text-muted hover:bg-mist hover:text-ink disabled:opacity-50"><RotateCcw size={15} strokeWidth={1.75} />Reset</button>}
    actions={<PrimaryButton type="button" onClick={save} loading={saving} loadingLabel="Saving" disabled={invalid || (!dirty && !saved)} className="h-11 px-5 text-small">{saved ? <><Check size={16} strokeWidth={2} />Saved</> : "Save"}</PrimaryButton>}>
    <div className="flex flex-col gap-5">
      {error && <p role="alert" className="rounded-md bg-danger-soft px-4 py-3 text-small text-danger">{error}</p>}
      <div>
        <DrawerGroup title="Live preview" hint="How the voice button and panel look on your website.">
          <VoiceWidgetPreview config={config} avatarUrl={avatarUrl} iconUrl={iconUrl} agentName={agent.agent_name} />
        </DrawerGroup>
        <DrawerGroup title="Colors">
          <div className="grid gap-4 sm:grid-cols-2">{COLORS.map((c) => <ColorField key={c.key} id={`vw-${c.key}`} label={c.label} hint={c.hint} value={config[c.key] || VOICE_WIDGET_DEFAULTS[c.key]} onChange={(v) => update(c.key, v)} />)}</div>
        </DrawerGroup>
        <DrawerGroup title="Button and avatar">
          <DrawerField label="Widget position"><ChoiceRow label="Widget position" value={config.position} options={Object.keys(POSITIONS)} render={(o) => POSITIONS[o]} onChange={(v) => update("position", v)} /></DrawerField>
          <DrawerField label={`Floating button size (${config.button_size}px)`} htmlFor="vw-size"><input id="vw-size" type="range" min={48} max={80} value={config.button_size} onChange={(e) => update("button_size", parseInt(e.target.value, 10))} className="w-full accent-ink" /></DrawerField>
          <AssetField label="Orb avatar" hint="Shown inside the orb circle. Recommended: 120×120px, square." url={avatarUrl} uploading={uploading === "orb_avatar"} onPick={(f) => upload("orb_avatar", "avatar", f)} onRemove={() => update("orb_avatar", "")} />
          <AssetField label="Button icon" hint="Custom image for the floating button. Recommended: 120×120px, square." url={iconUrl} uploading={uploading === "button_icon"} onPick={(f) => upload("button_icon", "icon", f)} onRemove={() => update("button_icon", "")} />
        </DrawerGroup>
        <DrawerGroup title="Embed code" hint="Copy this code and paste it on your website to show the voice widget.">
          <div className="flex flex-wrap gap-1 rounded-lg bg-mist p-1" role="tablist" aria-label="Embed platform">{PLATFORMS.map((p) => <button key={p.id} type="button" role="tab" aria-selected={platform === p.id} onClick={() => setPlatform(p.id)} className={`rounded-full px-3 py-1.5 text-caption font-semibold ${platform === p.id ? "bg-paper text-ink shadow-soft" : "text-muted hover:text-ink"}`}>{p.label}</button>)}</div>
          <p className="rounded-sm bg-sky-soft px-4 py-3 text-small text-sky-deep">{hint}</p>
          {keyReason && <AccessNotice compact reason={keyReason.kind === "error" ? { ...keyReason, message: `${String(keyReason.message || "The API key could not be loaded").replace(/[.!]?$/, ".")} The code below shows YOUR_API_KEY until it loads.` } : keyReason} onRetry={loadKey} />}
          <div className="relative min-w-0 rounded-sm bg-ink p-4 pt-14 text-paper sm:pr-28 sm:pt-4">
            {keyLoading ? <p className="flex items-center gap-2 font-mono text-caption text-paper/70"><InlineLoader size={14} />Loading API key...</p> : <pre className="whitespace-pre-wrap break-all font-mono [font-variant-ligatures:none] text-caption leading-relaxed">{code}</pre>}
            <button type="button" onClick={copyCode} disabled={keyLoading} className="absolute right-3 top-3 inline-flex h-9 items-center gap-1.5 rounded-full bg-paper/10 px-3 text-caption font-semibold text-paper hover:bg-paper/20 disabled:opacity-50">{copied ? <Check size={14} /> : <Clipboard size={14} />}{copied ? "Copied" : "Copy code"}</button>
          </div>
        </DrawerGroup>
      </div>
      <p className="flex items-center gap-2 text-caption text-muted"><Mic size={13} strokeWidth={1.75} />Changes reach the live widget the next time a visitor loads your site.</p>
    </div>
  </Drawer>;
}
